//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { TOOL_CARD_BORDER_CLASS, TOOL_CARD_BG_CLASS } from './toolCardStyle';

export interface TurnTraceControlProps {
  turnId: string;
  // Rejected calls excluded — a rejected call never ran, so "N tools
  // completed" must not count it alongside one that did (turnGrouping.ts's
  // displayToolCount/displayCompletedCount).
  toolCount: number;
  completedCount: number;
  isRunning: boolean;
  // Narrower than isRunning: true only when a call is genuinely executing,
  // never for a call merely awaiting approval. isRunning alone can't tell
  // "the agent is working" apart from "the agent is idle, waiting on you" —
  // a turn blocked purely on an approval decision was claiming "Running
  // tools…" while nothing ran and its own Approve/Reject card sat right
  // above it (found in review of #988, third round).
  hasActiveExecution: boolean;
  isOpen: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

// Rendered under a turn's answer as soon as its first non-internal tool call
// runs (not only once the turn finishes) — that's what lets the record be
// opened mid-run (AC4). A turn that dispatched no non-internal calls shows
// nothing at all: no control, no empty affordance.
const TurnTraceControl: React.FC<TurnTraceControlProps> = ({
  turnId,
  toolCount,
  completedCount,
  isRunning,
  hasActiveExecution,
  isOpen,
  onToggle,
  disabled = false,
}) => {
  const { t } = useTranslation();

  // No render gate here — the sole call site (ChatThread.tsx's
  // turnArtifactByLastMessageIndex) already excludes any turn with
  // visibleToolCount === 0 before this component is ever reached, including
  // an all-rejected turn (a rejected call is non-internal, so it counts
  // toward visibleToolCount even though displayToolCount/toolCount above
  // subtracts it back out). A second, independently-derived visibility
  // check here duplicated that decision with nothing tying the two
  // together (found in review of #988, fourth round — Constitution
  // Principle VII).
  //
  // While a turn is in flight there is no total to count towards — the agent
  // decides what to call next as it goes, so the client only ever knows how
  // many calls have FINISHED (FR-016). Anything of the "3 of 7" shape would
  // assert a denominator that does not exist and that visibly grows as
  // further calls arrive. A running tally, or nothing at all until the first
  // call resolves.
  //
  // The JS default values pluralise too, not just interpolate: uninitialised
  // i18next (the jest environment) returns the default verbatim, so leaving
  // {{count}} or a bare "s" in it would render literally in tests.
  const toolWord = (n: number) => (n === 1 ? 'tool' : 'tools');
  // hasActiveExecution is checked first, independent of completedCount — not
  // only inside the completedCount === 0 branch. A turn that already has one
  // finished call and then dispatches a second one that lands on
  // pending_approval still has isRunning true and hasActiveExecution false;
  // checking hasActiveExecution only in the zero-completed branch left that
  // case falling through to "N tools run so far", reintroducing the exact
  // "claims work is happening while it's actually just waiting on the user"
  // bug this component was already fixed for once, one branch up (found in
  // review of #988, fifth round).
  const countLabel = isRunning
    ? !hasActiveExecution
      ? t('ai-turn-trace-awaiting-tool-approvals', 'Awaiting tool approvals')
      : completedCount === 0
        ? t('ai-turn-trace-running', 'Running tools…')
        : t(
            'ai-turn-trace-tools-so-far',
            `${completedCount} ${toolWord(completedCount)} run so far`,
            { count: completedCount },
          )
    : t(
        'ai-turn-trace-tools-ran',
        `${toolCount} ${toolWord(toolCount)} completed`,
        { count: toolCount },
      );

  const toggleLabel = isOpen
    ? t('ai-turn-trace-hide-record', 'Hide what ran')
    : t('ai-turn-trace-show-record', 'Show what ran');

  return (
    <button
      type="button"
      id={`id-ai-module-turn-trace-control-${turnId}`}
      data-testid={`ai-module-turn-trace-control-${turnId}`}
      className={cn(
        'flex items-center gap-1.5 rounded-md border px-2 py-1',
        TOOL_CARD_BORDER_CLASS,
        TOOL_CARD_BG_CLASS,
        'text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        'disabled:pointer-events-none disabled:opacity-50',
      )}
      aria-expanded={isOpen}
      aria-controls={`ai-module-turn-trace-panel-${turnId}`}
      aria-label={`${countLabel} — ${toggleLabel}`}
      onClick={onToggle}
      disabled={disabled}
    >
      <span>{countLabel}</span>
      {isOpen ? (
        <ChevronUp
          className="size-3.5 shrink-0 text-slate-500"
          aria-hidden="true"
        />
      ) : (
        <ChevronDown
          className="size-3.5 shrink-0 text-slate-500"
          aria-hidden="true"
        />
      )}
    </button>
  );
};

export default TurnTraceControl;
